// src/utils/metaPixel.ts
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
  }
}

let loaded = false;

export const META_PIXEL_ID = '888604837651248';

// A second (legacy) Meta Pixel the admin wants running in parallel with the
// primary one. Meta's fbq dispatches every `track` event to ALL initialised
// pixel IDs, so listing both here makes PageView + every conversion event
// (ViewContent, InitiateCheckout, Purchase, CompleteRegistration, …) fire to
// BOTH pixels automatically — no changes needed at the call sites. (If you ever
// need to send an event to only one pixel, use `fbq('trackSingle', id, …)`.)
export const META_PIXEL_ID_LEGACY = '2366142850398849';
export const META_PIXEL_IDS = [META_PIXEL_ID, META_PIXEL_ID_LEGACY];

export function loadMetaPixel(pixelId: string) {
  if (typeof window === 'undefined') return;
  if (!pixelId) return;

  if (window.fbq && loaded) return;

  // Bootstrap script (no innerHTML)
  (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js'
  );

  // Initialise every configured pixel (primary + legacy), then a single
  // PageView — Meta fans that (and all later track events) out to every
  // initialised pixel.
  const ids = Array.from(new Set([...META_PIXEL_IDS, pixelId].filter(Boolean)));
  ids.forEach((id) => window.fbq?.('init', id));
  window.fbq?.('track', 'PageView');
  loaded = true;
}

export function trackViewContent(args: { contentName: string }) {
  window.fbq?.('track', 'ViewContent', {
    content_name: args.contentName,
    content_type: 'product'
  });
}

export function trackViewSpecialOfferContent(args: { contentName: string }) {
  window.fbq?.('track', 'ViewSpecialOffer', {
    content_name: args.contentName,
    content_type: 'product'
  });
}

export function trackInitiateCheckout(args: {
  contentId: string;
  contentName: string;
  value: number;
  currency?: string;
}) {
  const currency = args.currency ?? 'AUD';
  window.fbq?.('track', 'InitiateCheckout', {
    value: args.value,
    currency,
    content_type: 'product',
    content_ids: [args.contentId],
    content_name: args.contentName
  });
}

export function createMetaRegistrationEventId() {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);

  return `reg_${Date.now()}_${random}`;
}

export function getMetaEventSourceUrl() {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

export function trackCompleteRegistration(args: {
  eventId: string;
  valueAud?: number;
  contentName?: string;
}) {
  window.fbq?.(
    'track',
    'CompleteRegistration',
    {
      content_name: args.contentName ?? 'Account Registration',
      currency: 'AUD',
      value: args.valueAud ?? 0
    },
    { eventID: args.eventId }
  );
}

export function trackPurchase(args: {
  value: number;
  currency?: string;
  contentId: string;
  contentName: string;
  upsellPurchased: boolean;
  quantity?: number;
}) {
  const currency = args.currency ?? 'AUD';

  window.fbq?.('track', 'Purchase', {
    value: args.value,
    currency,
    content_type: 'product',
    content_ids: [args.contentId],

    contents: [
      {
        id: args.contentId,
        quantity: args.quantity ?? 1,
        item_price: args.value
      }
    ],

    content_name: args.contentName,

    // ✅ custom parameter (fine to send)
    upsellPurchased: args.upsellPurchased
  });
}
